import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { theme } from "antd";
import { BlockContent, blockInk, blockStyle } from "../Builder/LetterBlock";
import { defaultEmailStyles, templateBlocks, templateStyles } from "../blocks";

import styles from "./index.module.scss";

/** The width the letter is built at before it is scaled down — the sheet's. */
const SHEET_WIDTH = 640;

/**
 * How much of the cover the sheet takes across, leaving the ground showing
 * either side. The same relationship the builder's canvas has to its letter.
 */
const SHEET_SHARE = 0.8;

/**
 * What a template looks like, as the top of the letter itself.
 *
 * Not a picture of one: the same blocks the builder would open, drawn by the
 * same code the letter is, at the width the letter is really laid out at and
 * then scaled down to whatever the card gives it. A template that changes
 * changes here with it, and a thumbnail can never be out of date.
 *
 * The section's content and its band, not the whole LetterBlock. LetterBlock
 * registers itself with dnd-kit, and these cards sit inside the builder's
 * Blocks panel — which is inside the builder's own drag context. Three letters'
 * worth of sections would join it as drop targets and re-render on every
 * pointer move of a drag. A thumbnail has no business in a drag at all.
 *
 * Only the top shows. A letter is far taller than a card, and the part that
 * tells the templates apart — the header, the first picture, the first
 * paragraph — is all above the fold.
 *
 * The scale is measured rather than written down, because the covers are fluid:
 * three across in the picker, two in the builder's panel, and both narrow as
 * the window does.
 */
export default function TemplatePreview({ id }: { id: string }) {
  const { token } = theme.useToken();
  // The template's own answer for the letter as well as for its sections, so a
  // card stack is previewed on the ground it will actually open on.
  const emailStyles = useMemo(() => {
    const defaults = defaultEmailStyles(token);

    return { ...defaults, ...(templateStyles[id]?.(defaults) ?? {}) };
  }, [token, id]);
  const blocks = useMemo(() => templateBlocks[id]?.() ?? [], [id]);
  const cover = useRef<HTMLDivElement>(null);
  // 0 until the cover has been measured: a letter drawn at 640 and scaled a
  // frame later would flash across the card first.
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const element = cover.current;

    if (!element) return;

    const observer = new ResizeObserver(([entry]) =>
      setScale((entry.contentRect.width * SHEET_SHARE) / SHEET_WIDTH),
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    // Decoration, and inside the card's button: `inert` keeps the letter's own
    // links and buttons out of the tab order and out of the accessibility tree,
    // so the card is announced as the one action it is.
    <div ref={cover} className={styles.cover} inert>
      <div
        className={styles.sheet}
        style={{
          width: SHEET_WIDTH,
          background: emailStyles.background,
          fontFamily: emailStyles.fontFamily,
          transform: `scale(${scale})`,
        }}
      >
        {blocks.map((block) => (
          <div key={block.id} style={blockStyle(block, emailStyles)}>
            <BlockContent
              block={block}
              emailStyles={emailStyles}
              ink={blockInk(block, emailStyles)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
